import React from 'react';
import SpeechRecognition from 'react-speech-recognition';

import { isFirefox } from '../Utils';
import { hasPermissionsAPISupport, hasMediaDeviceAPISupport } from '../BrowserUtils';
import useCancelablePromise from '../hooks/useCancelablePromise';

const isSpeechToTextSupported = () => {
	if (isFirefox()) return false;
	if (!hasPermissionsAPISupport()) return false;
	return SpeechRecognition.browserSupportsSpeechRecognition();
};

export default function useSpeechToText() {
	const getCancelablePromise = useCancelablePromise();

	const [userConsent, setUserConsent] = React.useState<'denied' | 'granted' | 'not-granted' | 'default'>();

	const onUserAskPermissions = async () => {
		if (isFirefox()) return;
		if (!hasMediaDeviceAPISupport()) return;
		await navigator.mediaDevices
			.getUserMedia({ audio: true })
			.then(consent => {
				setUserConsent('granted');
			})
			.catch(e => {
				if (e.description === 'Permission denied') setUserConsent('not-granted');
				else if (e.description === 'Permission dismissed') setUserConsent('default');
			});
	};

	const isSpeechToTextEnabled = async () => {
		if (!hasPermissionsAPISupport()) return false;
		if (isFirefox()) return false;
		const permissionPromise = navigator.permissions.query({ name: 'microphone' as PermissionName });
		return getCancelablePromise(permissionPromise)
			.then(result => {
				if (result.state === 'granted') return true;
				return false;
			})
			.catch(() => {
				return false;
			});
	};

	React.useEffect(() => {
		if (isFirefox()) return;
		if (!hasPermissionsAPISupport()) return;
		const permissionPromise = navigator.permissions.query({ name: 'microphone' as PermissionName });
		getCancelablePromise(permissionPromise).then(result => {
			setUserConsent(result.state === 'prompt' ? 'default' : result.state);
		});
	}, [getCancelablePromise]);

	return {
		userConsent,
		onUserAskPermissions,
		isSpeechToTextSupported,
		isSpeechToTextEnabled
	};
}
